import { applyTextTransform, resolveMergeValue } from '@/lib/badgeTemplate'
import type { BadgePrintData } from '@/types/badgeDesigner'
import type {
  CertificateElement,
  CertificateEventData,
  CertificateMergeTagElement,
  CertificatePreset,
  CertificatePreviewRegistrant,
  CertificateTemplate,
  CertificateTextAlign,
  CertificateTextElement,
  CertificateTextStyle,
  CertificateTextTransform,
} from '@/types/certificateDesigner'

/**
 * The designer canvas works in 100-DPI pixels, the same unit the badge designer
 * uses. MUST equal `Certificates::PdfRenderer::DESIGN_DPI`: every stored
 * `x`/`y`/`width`/`height`/`font_size` is multiplied by PDF_DPI / DESIGN_DPI on
 * the way into the PDF, so a mismatch here silently rescales every design.
 */
export const CERT_DESIGN_DPI = 100

/** PDF user space, mirroring `Certificates::PdfRenderer::PDF_DPI`. */
export const CERT_PDF_DPI = 72

/** Mirrors `Certificates::PdfRenderer::SCALE`. */
export const CERT_PDF_SCALE = CERT_PDF_DPI / CERT_DESIGN_DPI

/**
 * The font families the designer offers.
 *
 * MUST stay in lockstep with the keys of `Certificates::PdfRenderer::FONT_FILES`
 * (app/services/certificates/pdf_renderer.rb), which are the family names Prawn
 * resolves against the vendored TTFs. A family listed here that the renderer
 * does not register falls back to Inter with no error and no warning, so the
 * design would look right in the browser and wrong in the PDF; a family added
 * there stays invisible until it is added here too.
 */
export const CERT_FONT_FAMILIES = ['Inter', 'Lora', 'JetBrains Mono', 'Great Vibes'] as const

export type CertificateFontFamily = (typeof CERT_FONT_FAMILIES)[number]

export const DEFAULT_CERT_FONT_FAMILY: CertificateFontFamily = 'Inter'

/**
 * What the canvas actually puts in `font-family`. Every family is loaded as a
 * webfont in app/frontend/entrypoints/application.css so the canvas matches the
 * PDF; the tail of each stack is only insurance for a blocked font request.
 */
export const CERT_FONT_CSS_STACKS: Record<CertificateFontFamily, string> = {
  Inter: '"Inter", "Helvetica Neue", Arial, sans-serif',
  Lora: '"Lora", Georgia, "Times New Roman", serif',
  'JetBrains Mono': '"JetBrains Mono", "SFMono-Regular", Consolas, monospace',
  'Great Vibes': '"Great Vibes", "Snell Roundhand", "Apple Chancery", cursive',
}

/**
 * Resolves a stored `font_family` to a CSS stack, falling back the same way
 * `Certificates::PdfRenderer#font_family` does — to Inter.
 */
export const certificateFontCss = (family: string): string =>
  CERT_FONT_CSS_STACKS[family as CertificateFontFamily] ??
  CERT_FONT_CSS_STACKS[DEFAULT_CERT_FONT_FAMILY]

/**
 * Mirrors `Certificates::PdfRenderer::BOLD_FONT_WEIGHT`. At most two faces are
 * vendored per family, so the renderer collapses every weight into bold or
 * regular at this threshold — which is why the designer offers exactly two
 * weights rather than the browser's full 100-900 ramp. Offering 500 would draw
 * a distinct weight on the canvas and regular in the PDF.
 */
export const CERT_BOLD_FONT_WEIGHT = 600

export const CERT_FONT_WEIGHT_OPTIONS: { label: string; value: string }[] = [
  { label: 'Regular', value: '400' },
  { label: 'Bold', value: '700' },
]

/**
 * The weights each family actually has a vendored face for. Mirrors the
 * `styles:` key of `Certificates::PdfRenderer::FONT_FILES`; the renderer derives
 * `SINGLE_WEIGHT_FONT_FAMILIES` from the same data.
 *
 * Great Vibes is published upstream as a single Regular (400) cut — there is no
 * bold. The renderer narrows a bold request on it back to regular on purpose,
 * so a designer that let a user pick Bold here would show a synthesised browser
 * bold on the canvas and a regular face in the PDF: the same silent divergence
 * the variable-font rule exists to prevent. Weight-picking is therefore driven
 * off this map rather than off a flat two-item list.
 */
export const CERT_FONT_FAMILY_WEIGHTS: Record<CertificateFontFamily, readonly string[]> = {
  Inter: ['400', '700'],
  Lora: ['400', '700'],
  'JetBrains Mono': ['400', '700'],
  'Great Vibes': ['400'],
}

/** The weights `family` can be drawn at, falling back the way the renderer does. */
export const certificateFontWeights = (family: string): readonly string[] =>
  CERT_FONT_FAMILY_WEIGHTS[family as CertificateFontFamily] ??
  CERT_FONT_FAMILY_WEIGHTS[DEFAULT_CERT_FONT_FAMILY]

/** True for a family that ships one face, so the weight control has nothing to offer. */
export const isSingleWeightCertificateFont = (family: string): boolean =>
  certificateFontWeights(family).length === 1

/** The weight options to show for `family` — never a weight the PDF cannot draw. */
export const certificateFontWeightOptions = (
  family: string
): { label: string; value: string }[] => {
  const available = certificateFontWeights(family)
  return CERT_FONT_WEIGHT_OPTIONS.filter((option) => available.includes(option.value))
}

/**
 * Snaps a weight onto one the family can actually be drawn at.
 *
 * This is what keeps an element that already carries `font_weight: '700'` from
 * rendering differently in the two places: switching its family to Great Vibes,
 * or loading a design saved while the family still had a bold face, would leave
 * 700 on the element, and the canvas would happily synthesise a fake bold that
 * the PDF never draws. Applied both on load (normalizeElement) and on every
 * family change in the designer.
 */
export const coerceCertificateFontWeight = (family: string, weight: string): string =>
  certificateFontWeights(family).includes(weight) ? weight : '400'

/** Mirrors `Certificates::PdfRenderer::MIN_FONT_SIZE`, in points. */
export const CERT_RENDERER_MIN_FONT_SIZE_PT = 6

/**
 * The smallest font size worth offering: anything below this is clamped back up
 * to 6pt by the renderer, so the canvas and the PDF would disagree.
 */
export const CERT_MIN_FONT_SIZE_PX = Math.ceil(CERT_RENDERER_MIN_FONT_SIZE_PT / CERT_PDF_SCALE)

export const CERT_MAX_FONT_SIZE_PX = 300

/**
 * Page dimensions in inches. `CertificateTemplate` validates 0 < size <= 20, and
 * the a4_landscape numbers are the column defaults in the migration.
 */
export const MIN_CERT_DIMENSION_IN = 1
export const MAX_CERT_DIMENSION_IN = 20

export type FixedCertificatePreset = Exclude<CertificatePreset, 'custom'>

export const CERT_PRESET_DIMENSIONS: Record<
  FixedCertificatePreset,
  { width_in: number; height_in: number }
> = {
  a4_landscape: { width_in: 11.69, height_in: 8.27 },
  a4_portrait: { width_in: 8.27, height_in: 11.69 },
  letter_landscape: { width_in: 11, height_in: 8.5 },
  letter_portrait: { width_in: 8.5, height_in: 11 },
}

/**
 * The preset menu. Values mirror `CertificateTemplate::PRESETS` exactly —
 * the model refuses to save anything else.
 */
export const CERT_PRESETS: { label: string; value: CertificatePreset }[] = [
  { label: 'A4 Landscape (11.69 x 8.27 in)', value: 'a4_landscape' },
  { label: 'A4 Portrait (8.27 x 11.69 in)', value: 'a4_portrait' },
  { label: 'Letter Landscape (11 x 8.5 in)', value: 'letter_landscape' },
  { label: 'Letter Portrait (8.5 x 11 in)', value: 'letter_portrait' },
  { label: 'Custom', value: 'custom' },
]

export const DEFAULT_CERT_PRESET: CertificatePreset = 'a4_landscape'

const DEFAULT_TEXT_STYLE = {
  font_family: DEFAULT_CERT_FONT_FAMILY as string,
  font_size: 40,
  font_weight: '400',
  text_align: 'center' as CertificateTextAlign,
  text_transform: 'none' as CertificateTextTransform,
  color: '#111827',
}

/** The style a freshly inserted text or merge-tag box starts with. */
export const createDefaultCertificateTextStyle = (): CertificateTextStyle => ({
  ...DEFAULT_TEXT_STYLE,
})

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const toNumber = (value: unknown, fallback: number, min = 0): number => {
  const num = Number(value)
  if (!Number.isFinite(num)) return fallback
  return Math.max(min, num)
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

export const createCertificateElementId = () =>
  `${Date.now()}-${Math.random().toString(16).slice(2)}`

export const isFixedCertificatePreset = (
  preset: CertificatePreset
): preset is FixedCertificatePreset => preset !== 'custom'

/**
 * A blank A4 landscape page. Used only when the server sends no template at all
 * — a brand new event still arrives with a built-but-unsaved record whose
 * `elements` is an empty array, and that empty array wins over these defaults.
 */
export const createDefaultCertificateTemplate = (): CertificateTemplate => ({
  preset: DEFAULT_CERT_PRESET,
  ...CERT_PRESET_DIMENSIONS[DEFAULT_CERT_PRESET],
  elements: [],
  background_image_url: null,
})

const normalizeTextAlign = (value: unknown): CertificateTextAlign => {
  if (value === 'center' || value === 'right' || value === 'left') return value
  return DEFAULT_TEXT_STYLE.text_align
}

const normalizeTextTransform = (value: unknown): CertificateTextTransform => {
  if (value === 'uppercase' || value === 'capitalize' || value === 'none') return value
  return 'none'
}

/**
 * Snaps a stored family back onto the offered list. A design saved before a
 * family was retired would otherwise show a font in the designer that the
 * renderer no longer has, which is exactly the silent-fallback trap the
 * CERT_FONT_FAMILIES comment describes.
 */
const normalizeFontFamily = (value: unknown): string => {
  const family = String(value || '')
  return (CERT_FONT_FAMILIES as readonly string[]).includes(family)
    ? family
    : DEFAULT_CERT_FONT_FAMILY
}

/**
 * Collapsed to the faces the renderer can actually embed for this family: first
 * to the two-value ramp at the bold threshold, then down to a weight the family
 * ships. The second step is what makes a stored `700` on a single-weight family
 * — from a family switch, a hand-edited payload, or a family that later loses
 * its bold cut — show on the canvas exactly as the PDF will draw it.
 */
const normalizeFontWeight = (value: unknown, family: string): string => {
  const weight = Number(value)
  const collapsed = !Number.isFinite(weight)
    ? DEFAULT_TEXT_STYLE.font_weight
    : weight >= CERT_BOLD_FONT_WEIGHT
      ? '700'
      : '400'
  return coerceCertificateFontWeight(family, collapsed)
}

const normalizeElement = (raw: unknown, index: number): CertificateElement | null => {
  if (!isObject(raw)) return null

  const type = String(raw.type || '')
  if (type !== 'text' && type !== 'merge_tag') return null

  const base = {
    id: String(raw.id || createCertificateElementId()),
    x: toNumber(raw.x, 0),
    y: toNumber(raw.y, 0),
    width: toNumber(raw.width, 240, 1),
    height: toNumber(raw.height, 70, 1),
    z_index: Number.isFinite(Number(raw.z_index)) ? Number(raw.z_index) : index,
  }

  const family = normalizeFontFamily(raw.font_family)
  const style = {
    font_family: family,
    font_size: clamp(
      toNumber(raw.font_size, DEFAULT_TEXT_STYLE.font_size, CERT_MIN_FONT_SIZE_PX),
      CERT_MIN_FONT_SIZE_PX,
      CERT_MAX_FONT_SIZE_PX
    ),
    font_weight: normalizeFontWeight(raw.font_weight, family),
    text_align: normalizeTextAlign(raw.text_align),
    text_transform: normalizeTextTransform(raw.text_transform),
    color: String(raw.color || DEFAULT_TEXT_STYLE.color),
  }

  if (type === 'text') {
    return {
      ...base,
      ...style,
      type: 'text',
      text: String(raw.text ?? 'Text'),
    } satisfies CertificateTextElement
  }

  return {
    ...base,
    ...style,
    type: 'merge_tag',
    merge_key: String(raw.merge_key || 'full_name'),
  } satisfies CertificateMergeTagElement
}

const normalizePreset = (value: unknown): CertificatePreset => {
  const preset = String(value || '')
  const match = CERT_PRESETS.find((option) => option.value === preset)
  return match ? match.value : DEFAULT_CERT_PRESET
}

export const normalizeCertificateTemplate = (rawTemplate?: unknown): CertificateTemplate => {
  const fallback = createDefaultCertificateTemplate()
  if (!isObject(rawTemplate)) return fallback

  const preset = normalizePreset(rawTemplate.preset)
  const dimensions = isFixedCertificatePreset(preset)
    ? CERT_PRESET_DIMENSIONS[preset]
    : {
        width_in: clamp(
          toNumber(rawTemplate.width_in, fallback.width_in, MIN_CERT_DIMENSION_IN),
          MIN_CERT_DIMENSION_IN,
          MAX_CERT_DIMENSION_IN
        ),
        height_in: clamp(
          toNumber(rawTemplate.height_in, fallback.height_in, MIN_CERT_DIMENSION_IN),
          MIN_CERT_DIMENSION_IN,
          MAX_CERT_DIMENSION_IN
        ),
      }

  const elements = Array.isArray(rawTemplate.elements)
    ? (rawTemplate.elements
        .map((element, index) => normalizeElement(element, index))
        .filter(Boolean) as CertificateElement[])
    : fallback.elements

  return {
    // Guarded against null rather than run through Number.isFinite: the server
    // sends `id: null` for the built-but-unsaved template a brand new event
    // gets, and Number(null) is 0 — a perfectly finite id that does not exist.
    id: typeof rawTemplate.id === 'number' ? rawTemplate.id : undefined,
    preset,
    ...dimensions,
    elements,
    background_image_url:
      typeof rawTemplate.background_image_url === 'string'
        ? rawTemplate.background_image_url
        : null,
  }
}

/**
 * Re-stamps `z_index` from the layer order. `Certificates::PdfRenderer` sorts on
 * this key, and Ruby's `sort_by` is not stable — leaving every element on the
 * same value would make the paint order of overlapping boxes arbitrary between
 * renders. Applied on the way out so what the Layers list shows is what the PDF
 * draws.
 */
export const withSequentialZIndex = (elements: CertificateElement[]): CertificateElement[] =>
  elements.map((element, index) => ({ ...element, z_index: index }))

/** Forces the page size a fixed preset implies; `custom` keeps whatever was typed. */
export const ensureCertificatePresetConsistency = (
  template: CertificateTemplate
): CertificateTemplate => {
  if (!isFixedCertificatePreset(template.preset)) {
    return {
      ...template,
      width_in: clamp(template.width_in, MIN_CERT_DIMENSION_IN, MAX_CERT_DIMENSION_IN),
      height_in: clamp(template.height_in, MIN_CERT_DIMENSION_IN, MAX_CERT_DIMENSION_IN),
    }
  }

  return { ...template, ...CERT_PRESET_DIMENSIONS[template.preset] }
}

export const certificateCanvasSizeInPixels = (
  template: Pick<CertificateTemplate, 'width_in' | 'height_in'>,
  dpi: number = CERT_DESIGN_DPI
) => ({
  width: Math.max(1, Math.round(template.width_in * dpi)),
  height: Math.max(1, Math.round(template.height_in * dpi)),
})

/**
 * The canvas preview subject. `resolveMergeValue` is reused verbatim from the
 * badge designer — the two designers resolve the same bare-key vocabulary
 * against the same registrant/event shapes.
 */
export const buildCertificatePreviewData = (
  registrant: CertificatePreviewRegistrant | null,
  event?: CertificateEventData
): BadgePrintData | undefined => (registrant ? { registrant, event } : undefined)

/**
 * What the canvas shows for one element. Mirrors
 * `Certificates::PdfRenderer#draw_text`: resolve the merge key (or take the
 * literal text), then apply the case transform.
 */
export const resolveCertificateElementText = (
  element: CertificateElement,
  data?: BadgePrintData
): string => {
  const rawText =
    element.type === 'text' ? element.text : resolveMergeValue(element.merge_key, data)
  return applyTextTransform(rawText, element.text_transform)
}
